import { faLayerGroup } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createRoot } from 'react-dom/client';
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import TranslationsProvider, { useTranslation } from '@utils/i18n/i18n';
import translator from '@App/app.translator';
import { useOnClickOutside } from '@services/hooks.service';
import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';

import {
  hideSatelliteLayer,
  showSatelliteLayer,
} from './layer-control.utils';

import './layer-control.css';

const LAYER_OPTIONS = {
  basic: 'basic',
  satellite: 'satellite',
};

function LayerToggle({ map }) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState(LAYER_OPTIONS.basic);
  const containerRef = useRef(null);

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
  };

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (selectedLayer === LAYER_OPTIONS.satellite) {
      showSatelliteLayer(map);
      return;
    }

    hideSatelliteLayer(map);
  }, [map, selectedLayer]);

  const handleLayerSelect = (layer) => {
    setSelectedLayer(layer);
  };

  return (
    <div className="layer-toggle-container" ref={containerRef}>
      <div className="maplibregl-ctrl-group layer-toggle-group">
        <button
          type="button"
          className={`layer-toggle ${isOpen ? 'open' : ''}`}
          onClick={toggleOpen}
          title={t('Select basemap layer')}
        >
          <FontAwesomeIcon icon={faLayerGroup} />
        </button>
      </div>
      {isOpen && (
        <div className="layer-toggle-popover">
          <button
            type="button"
            className={`layer-option layer-option--basic ${selectedLayer === LAYER_OPTIONS.basic ? 'selected' : ''}`}
            onClick={() => handleLayerSelect(LAYER_OPTIONS.basic)}
            title={t('Basic map')}
          >
            <img src="/img/misc/basic.png" alt={t('Basic map')} className="layer-option__preview" />
            <span className="layer-option__label">{t('Basic')}</span>
          </button>
          <button
            type="button"
            className={`layer-option layer-option--satellite ${selectedLayer === LAYER_OPTIONS.satellite ? 'selected' : ''}`}
            onClick={() => handleLayerSelect(LAYER_OPTIONS.satellite)}
            title={t('Satellite map')}
          >
            <img src="/img/misc/satellite.png" alt={t('Satellite map')} className="layer-option__preview" />
            <span className="layer-option__label">{t('Satellite')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

class MaplibreLayerControl {
  onAdd(map) {
    this._isRemoved = false;
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'maplibregl-ctrl layer-control';
    this._root = createRoot(this._container);
    this._root.render(
      <TranslationsProvider translator={translator}>
        <LayerToggle map={map} />
      </TranslationsProvider>,
    );
    return this._container;
  }

  onRemove() {
    if (this._isRemoved) {
      return;
    }

    this._isRemoved = true;

    const root = this._root;
    const container = this._container;

    this._root = undefined;
    this._container = undefined;
    this._map = undefined;

    if (container?.parentNode) {
      container.parentNode.removeChild(container);
    }

    queueMicrotask(() => {
      root?.unmount();
    });
  }
}

function LayerControl() {
  const { map, isLoading } = useMaplibreMap();

  useEffect(() => {
    if (!map || isLoading) return;

    const layerControl = new MaplibreLayerControl();
    map.addControl(layerControl, 'top-right');

    return () => {
      if (layerControl._map) {
        map.removeControl(layerControl);
      }
    };
  }, [map, isLoading]);

  return null;
}

export default LayerControl;
